import { CodeEditor } from '@acrodata/code-editor';
import { ChangeDetectionStrategy,Component, computed, inject, OnDestroy, OnInit, Signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { languages } from '@codemirror/language-data';
import { forkJoin } from 'rxjs';

import { AdminComponentBase, PageSimpleContentComponent, PageStatus } from 'app/layout/content';
import { ErrorComponent } from 'app/shared/components/error.component';
import { SaveButtonComponent } from 'app/shared/components/save-button.component';
import { ConfirmDialogService } from 'app/shared/dialog/confirm-dialog-service';

import { AddLocaleDialogComponent } from './add-locale-dialog.component';
import { DesignerCustomizationsService } from './designer-customizations.service';

@Component({
    templateUrl: './store-designer-custom-locales-component.component.html',
    styleUrls: ['./store-designer-custom-locales-component.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, MatIcon, MatButton, SaveButtonComponent, ErrorComponent, MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, FormsModule, CodeEditor]
})
export class StoreDesignerCustomLocalesComponentComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly designerCustomizationsService = inject(DesignerCustomizationsService);
    private readonly dialog = inject(MatDialog);
    private readonly confirmDialogService = inject(ConfirmDialogService);

    storeId: string;
    cultures: string[] = [];
    localesContent: Record<string, string> = {};
    savedCulture: Record<string, string> = {};
    languages = languages

    ngOnInit(): void {
        this.registerSubscription(this.route.params.subscribe(params => {
            this.storeId = params.storeId;
            this.subscribeWithGenericLoadingErrorHandling(this.designerCustomizationsService.getStoreFiles(this.storeId),
                customization => {
                    this.subscribeWithGenericLoadingErrorHandling(
                        forkJoin(customization.locales.map(s => this.designerCustomizationsService.getCustomizationFile(s.url))), (files) => {
                            for (let i = 0; i < customization.locales.length; i++) {
                                let locale = customization.locales[i];
                                let content = files[i];
                                this.localesContent[locale.culture] = content;
                                this.savedCulture[locale.culture] = content;
                            }
                            this.cultures = customization.locales.map(x => x.culture);

                        });
                });
        }));
    }

    ngOnDestroy(): void {
        super.unsubscribeSubscriptions();
    }

    addCulture() {
        this.dialog.open(AddLocaleDialogComponent).afterClosed().subscribe((result: string) => {
            if (!result) {
                return;
            }
            let culture = result.toLowerCase();
            if (culture in this.localesContent) {
                return;
            }
            this.cultures.push(culture);
            this.localesContent[culture] = '{\n}';
        });
    }

    save(culture: string) {
        let content = this.localesContent[culture];
        this.subscribeWithGenericSavingErrorHandling(this.designerCustomizationsService.updateLocale({
            storeId: this.storeId,
            content: content,
            culture: culture
        }), () => {
            this.savedCulture[culture] = content;
        });
    }

    saveAll() {
        let cultureToSave = this.cultures
            .filter(culture => this.isCultureNotSaved(culture))
            .map(culture => this.designerCustomizationsService.updateLocale({
                storeId: this.storeId,
                content: this.localesContent[culture],
                culture: culture
            }));
        this.subscribeWithGenericSavingErrorHandling(forkJoin(cultureToSave), () => {
            for (let culture of this.cultures) {
                this.savedCulture[culture] = this.localesContent[culture];
            }
        });
    }

    public canDelete(culture: string): Signal<boolean> {
        return computed(() => this.pageStatus === PageStatus.loaded && this.savedCulture[culture] !== undefined);
    }

    delete(culture: string): void {
        this.confirmDialogService
            .confirm(
                'Confirmation',
                `Do you want to delete the locale '${culture}' for this store?`
            ).subscribe(() => {
                this.subscribeWithGenericSavingErrorHandling(this.designerCustomizationsService.deleteLocale({
                    storeId: this.storeId,
                    culture: culture
                }), () => {
                    this.cultures = this.cultures.filter(x => x !== culture);
                    delete this.localesContent[culture];
                    delete this.savedCulture[culture];
                });
            }
        );
    }

    isCultureNotSaved(culture?: string): Signal<boolean> {
        return computed(() => {
            if (culture) {
                return this.localesContent[culture] !== this.savedCulture[culture];
            }
            for (let culture of this.cultures) {
                if (this.localesContent[culture] !== this.savedCulture[culture])
                    return true;
            }
            return false;
        })
    }

    async updateLocaleFromFile(culture: string, files: FileList) {
        if (!files.length) {
            return;
        }
        this.localesContent[culture] = await files[0].text();
    }

    async updateLocalesFromFile(files: FileList) {
        if (!files.length) {
            return;
        }

        for (let i = 0; i < files.length; i++) {
            let file = files[i];
            let content = await file.text();
            const cultureMatch = file.name.match(/\b[a-z]{2}(-[A-Z]{2})?\b/i);
            if (!cultureMatch) {
                continue;
            }
            const culture = cultureMatch[0];
            this.localesContent[culture] = content;
            if (!this.cultures.includes(culture)) {
                this.cultures.push(culture);
            }
        }
    }

    handleSaveEditor(event: KeyboardEvent, culture: string) {
        if (event.key === 's' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault(); // Prevent browser's save dialog
            this.save(culture);
        }
    }
}
